/* v2 content pages, search and 404 (workstream "pages").
   One small custom element per section; every listener is removed on disconnect so theme-editor
   reloads and re-renders never double-bind. No dependencies. ES2017, old-Chrome safe. */
(function () {
  if (window.customElements && customElements.get('xz-pg-section')) return;

  class XzPgSection extends HTMLElement {}

  XzPgSection.prototype.connectedCallback = function () {
    this._off = [];
    this._observers = [];
    this._aborts = [];
    this.initFaq();
    this.initRise();
    this.initForm();
    this.initSearch();
    this.initReviews();
  };

  XzPgSection.prototype.disconnectedCallback = function () {
    (this._off || []).forEach(function (fn) { fn(); });
    (this._observers || []).forEach(function (o) { o.disconnect(); });
    (this._aborts || []).forEach(function (c) { try { c.abort(); } catch (e) {} });
    this._off = []; this._observers = []; this._aborts = [];
  };

  XzPgSection.prototype.on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    this._off.push(function () { el.removeEventListener(type, fn, opts); });
  };

  /* Milen 1_7 (8 Oct): "make the entire container of a single question (and answer) clickable to open and close".
     A container click runs the button's own toggle; the button stays the one keyboard/ARIA control (no role or tabindex
     on the container). Left alone: a click on a control inside the item (the button runs its own listener, so it
     toggles once; answer links navigate), one that ends a text selection inside the item, a modified click, and the
     repeat clicks of a double/triple click (word selection after the first click has toggled). */
  var FAQ_CONTROLS = 'a[href],button,input,select,textarea,label,summary,details,iframe,video,audio,[contenteditable],[tabindex],[role=button],[role=link]';
  function faqContainerClick(event, item) {
    if (event.defaultPrevented || event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.detail > 1) return false;
    var target = event.target && event.target.nodeType === 1 ? event.target : event.target && event.target.parentElement;
    var control = target && target.closest(FAQ_CONTROLS);
    if (control && item.contains(control)) return false;
    var selection = window.getSelection ? window.getSelection() : null;
    if (selection && !selection.isCollapsed) {
      for (var i = 0; i < selection.rangeCount; i++) if (selection.getRangeAt(i).intersectsNode(item)) return false;
    }
    return true;
  }

  /* FAQ: Milen's xc-sc-faqs. Answer sits between question and the 40px circle button; instant toggle. The whole
     question container (.xz-pg-faq) toggles too; its listener sits on the container itself, because iOS Safari sends
     no click to a plain element whose only listener is delegated from the document. */
  XzPgSection.prototype.initFaq = function () {
    var self = this;
    this.querySelectorAll('[data-pg-faq]').forEach(function (btn) {
      var answer = document.getElementById(btn.getAttribute('aria-controls'));
      if (!answer) return;
      function toggle() {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        answer.hidden = !open;
      }
      self.on(btn, 'click', toggle);
      var item = btn.closest('.xz-pg-faq');
      if (item && self.contains(item)) self.on(item, 'click', function (event) { if (faqContainerClick(event, item)) toggle(); });
    });
  };

  /* Motion: headers rise 5px and fade (800ms move / 1500ms opacity, outQuad, 0/200/400/600 stagger). */
  XzPgSection.prototype.initRise = function () {
    var items = Array.prototype.slice.call(this.querySelectorAll('[data-pg-rise]'));
    if (!items.length) return;
    /* The CSS hides headers from the first paint (html.xz-v2-js); anything that will not animate shows now. */
    if (!('IntersectionObserver' in window) ||
        (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
        (window.Shopify && window.Shopify.designMode)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    this.classList.add('xz-pg--motion');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var group = el.parentElement ? Array.prototype.filter.call(el.parentElement.children, function (c) { return c.hasAttribute('data-pg-rise'); }) : [el];
        var idx = Math.max(0, group.indexOf(el));
        setTimeout(function () { el.classList.add('is-in'); }, Math.min(idx, 3) * 200);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -7% 0px' });
    items.forEach(function (el) { io.observe(el); });
    this._observers.push(io);
  };

  /* Contact form: native constraint validation with Milen-style inline messages and a character counter. */
  XzPgSection.prototype.initForm = function () {
    var self = this;
    var form = this.querySelector('form.xz-pg-form');
    if (!form) return;
    form.setAttribute('novalidate', '');
    var fields = Array.prototype.slice.call(form.querySelectorAll('.xz-pg-input'));

    function message(input) {
      var v = input.validity;
      if (v.valueMissing) return input.getAttribute('data-msg-required') || 'This field is required.';
      if (v.typeMismatch && input.type === 'email') return 'Enter an email address like name@example.com.';
      if (v.patternMismatch) return input.getAttribute('data-msg-pattern') || 'Check the format of this field.';
      if (v.tooLong) return 'Keep this under ' + input.maxLength + ' characters.';
      if (v.tooShort) return 'Add a little more detail (at least ' + input.minLength + ' characters).';
      return '';
    }
    function show(input) {
      var err = document.getElementById(input.getAttribute('data-error-id'));
      var msg = input.checkValidity() ? '' : message(input);
      if (err) { err.textContent = msg; err.hidden = !msg; }
      if (msg) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
      return !msg;
    }
    /* Pressing SEND blurs the focused field first; a blur message there would push the button away before the
       click lands. Blur validation waits while the pointer is down on the button; submit validates everything. */
    var pressing = false;
    var submitBtn = form.querySelector('[type=submit]');
    var release = function () { setTimeout(function () { pressing = false; }, 0); };
    if (submitBtn) {
      ['pointerdown', 'mousedown', 'touchstart'].forEach(function (t) {
        self.on(submitBtn, t, function () { pressing = true; }, { passive: true });
      });
      this.on(document, 'pointerup', release);
      this.on(document, 'mouseup', release);
      this.on(document, 'touchend', release);
      this.on(document, 'touchcancel', release);
    }
    fields.forEach(function (input) {
      self.on(input, 'blur', function (event) {
        if (pressing || (submitBtn && event.relatedTarget === submitBtn)) return;
        if (input.value || input.hasAttribute('aria-invalid')) show(input);
      });
      self.on(input, 'input', function () {
        if (input.hasAttribute('aria-invalid')) show(input);
        var counter = document.getElementById(input.getAttribute('data-count-id'));
        if (counter && input.maxLength > 0) counter.textContent = input.value.length + '/' + input.maxLength;
      });
    });
    var busyTimer = null;
    /* Shopify's storefront captcha binds its own submit listener to this form (on first focus) that always
       prevents default and then submits programmatically, ignoring defaultPrevented. Validation therefore runs
       in the capture phase on the document, which fires before any listener on the form itself, and an
       invalid or repeated submit is stopped outright so the captcha listener never sees it. */
    var block = function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
    };
    this.on(document, 'submit', function (event) {
      if (event.target !== form) return;
      pressing = false;
      if (form.getAttribute('data-pg-busy') === 'true') { block(event); return; }
      var firstBad = null;
      fields.forEach(function (input) { if (!show(input) && !firstBad) firstBad = input; });
      if (firstBad) {
        block(event);
        /* centre the field with its label: a plain focus() scroll parks it under the fixed promo bar and nav */
        var box = firstBad.closest ? firstBad.closest('.xz-pg-field') : null;
        try { (box || firstBad).scrollIntoView({ block: 'center' }); firstBad.focus({ preventScroll: true }); }
        catch (e) { firstBad.focus(); }
        return;
      }
      /* one message per press: ignore repeat submits while this one is in flight (Shopify's captcha step
         submits programmatically, which doesn't fire this event); release after 10s in case it is dismissed */
      form.setAttribute('data-pg-busy', 'true');
      if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');
      clearTimeout(busyTimer);
      busyTimer = setTimeout(function () {
        form.removeAttribute('data-pg-busy');
        if (submitBtn) submitBtn.removeAttribute('aria-disabled');
      }, 10000);
    }, true);
    this._off.push(function () { clearTimeout(busyTimer); });
  };

  /* Search: counts for the inactive tabs, sort auto-submit, filter sheet, progressive LOAD MORE. */
  XzPgSection.prototype.initSearch = function () {
    var self = this;
    /* narrow phones: the pill row scrolls sideways; bring the selected pill fully into view (row only, the
       page itself never scrolls). Re-run when the counts arrive, since they change the pill widths. */
    var tabRow = this.querySelector('.xz-pg-tabs');
    var revealTab = function (target) {
      if (!tabRow) return;
      var act = target && target.nodeType === 1 ? target : tabRow.querySelector('[aria-current=page]');
      if (!act || tabRow.scrollWidth <= tabRow.clientWidth + 1) return;
      var rowBox = tabRow.getBoundingClientRect();
      var actBox = act.getBoundingClientRect();
      var rowStyle = window.getComputedStyle(tabRow);
      var visibleRight = rowBox.right - (parseFloat(rowStyle.paddingRight) || 0);
      var visibleLeft = rowBox.left + (parseFloat(rowStyle.paddingLeft) || 0);
      if (actBox.right > visibleRight) tabRow.scrollLeft += Math.ceil(actBox.right - visibleRight) + 2;
      else if (actBox.left < visibleLeft) tabRow.scrollLeft -= Math.ceil(visibleLeft - actBox.left);
    };
    revealTab();
    if (tabRow) {
      this.on(window, 'resize', function () { revealTab(); });
      /* keyboard: a focused pill (and its focus ring) is brought into view inside the row, never by page scroll */
      this.on(tabRow, 'focusin', function (event) {
        var pill = event.target && event.target.closest ? event.target.closest('[data-pg-tab]') : null;
        if (pill) revealTab(pill);
      });
    }
    /* zero results on this tab but another tab has some: say so and link to it (first tab with results wins) */
    var alt = this.querySelector('[data-pg-alt]');
    var hint = this.querySelector('[data-pg-hint]');
    var altOrder = Array.prototype.slice.call(this.querySelectorAll('[data-pg-count-url]'));
    var altCounts = [];
    var showAlt = function () {
      if (!alt || alt.getAttribute('data-pg-alt-set') === 'true') return;
      for (var i = 0; i < altOrder.length; i++) {
        var c = altCounts[i];
        if (c === undefined) return; /* wait for earlier tabs so the order stays Products, Articles, Pages */
        if (c > 0) {
          var tab = altOrder[i].closest('[data-pg-tab]');
          var label = tab && tab.firstElementChild ? tab.firstElementChild.textContent.trim().toLowerCase() : 'results';
          if (c === 1) label = label.replace(/s$/, '');
          var link = document.createElement('a');
          link.href = tab ? tab.getAttribute('href') : '#';
          link.textContent = 'See ' + c + ' ' + label + ' \u2192';
          alt.textContent = '';
          alt.appendChild(link);
          alt.hidden = false;
          alt.setAttribute('data-pg-alt-set', 'true');
          if (hint) hint.hidden = true;
          return;
        }
      }
    };
    this.querySelectorAll('[data-pg-count-url]').forEach(function (el, index) {
      var url = el.getAttribute('data-pg-count-url');
      if (!url || !window.fetch) return;
      var ctrl = window.AbortController ? new AbortController() : null;
      if (ctrl) self._aborts.push(ctrl);
      fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (t) {
          var n = parseInt(String(t).replace(/[^0-9]/g, ''), 10);
          if (isNaN(n)) { altCounts[index] = 0; showAlt(); return; }
          el.textContent = '(' + n + ')';
          altCounts[index] = n;
          showAlt();
          var tab = el.closest('[data-pg-tab]');
          if (tab && n === 0 && tab.hasAttribute('data-pg-hide-empty')) tab.hidden = true;
          revealTab();
        })
        .catch(function () { altCounts[index] = 0; showAlt(); });
    });

    var filtered = this.querySelector('[data-pg-empty-filtered]');
    var plain = this.querySelector('[data-pg-empty-default]');
    if (filtered && plain && /[?&]filter\./.test(window.location.search)) { filtered.hidden = false; plain.hidden = true; }

    /* filter forms: never send empty price params; the desktop column applies on change, like the PLP */
    var tidy = function (form) {
      /* Min above Max (300 / 100) means the range between them, as on the PLP: the fields swap before submitting */
      var low = form.querySelector('.xz-pg-price input[name$=".gte"]');
      var high = form.querySelector('.xz-pg-price input[name$=".lte"]');
      if (low && high && low.value !== '' && high.value !== '' && Number(low.value) > Number(high.value)) {
        var swap = low.value;
        low.value = high.value;
        high.value = swap;
      }
      Array.prototype.forEach.call(form.querySelectorAll('input[type=number]'), function (input) {
        if (input.value === '') input.disabled = true;
      });
    };
    this.querySelectorAll('form[id^="SearchForm-"]').forEach(function (form) {
      self.on(form, 'submit', function () { tidy(form); });
      if (!form.hasAttribute('data-pg-autosubmit')) return;
      /* enhanced: the no-JS APPLY FILTERS button hides (CSS), exactly like the PLP's column */
      form.setAttribute('data-pg-enhanced', '');
      self.on(form, 'change', function (event) {
        if (event.target && event.target.type === 'number') return;
        tidy(form);
        form.submit();
      });
      /* price applies once focus leaves both inputs (Enter still submits), never mid-typing, and only when it
         differs from the current URL, as on the PLP */
      self.on(form, 'focusout', function (event) {
        var target = event.target;
        if (!target || target.type !== 'number') return;
        var group = target.closest('.xz-pg-price');
        if (!group || (event.relatedTarget && group.contains(event.relatedTarget))) return;
        var params = new URLSearchParams(window.location.search);
        var changed = Array.prototype.some.call(group.querySelectorAll('input[type=number]'), function (input) {
          return String(input.value).trim() !== String(params.get(input.name) || '').trim();
        });
        if (!changed) return;
        tidy(form);
        form.submit();
      });
    });
    /* re-enable after a bfcache restore so the fields stay editable. On Back the desktop column also drops the ticks
       the browser put back (Chrome's form-state restore, or the back/forward cache keeping the tick that navigated):
       they are not this page's applied filters, and the next tick would send them too (r4, the column's twin of
       filtering-r4-2). reset() fires no change event, so nothing submits; a price being typed is left alone. */
    this.on(window, 'pageshow', function () {
      self.querySelectorAll('form[id^="SearchForm-"][data-pg-autosubmit]').forEach(function (form) {
        if (!form.contains(document.activeElement)) form.reset();
      });
      self.querySelectorAll('form[id^="SearchForm-"] input[type=number][disabled]').forEach(function (i) { i.disabled = false; });
    });

    var sheet = this.querySelector('[data-pg-sheet]');
    if (sheet) {
      /* Unapplied choices are discarded, as on the PLP sheet (filtering-r4-2): any close other than APPLY (X,
         Escape, backdrop) puts the sheet's form back to its server-rendered defaults, which are the applied
         filters, sort and prices, so it reopens on the results shown. Opening and pageshow do the same, so ticks
         a browser restored into the form on Back never show as applied. APPLY navigates without closing the sheet. */
      var sheetForm = sheet.querySelector('form[id^="SearchForm-"]');
      var discard = function () {
        if (!sheetForm) return;
        sheetForm.reset();
        Array.prototype.forEach.call(sheetForm.querySelectorAll('input[type=number][disabled]'), function (i) { i.disabled = false; });
      };
      this.querySelectorAll('[data-pg-sheet-open]').forEach(function (b) {
        self.on(b, 'click', function () {
          discard();
          if (typeof sheet.showModal === 'function') sheet.showModal(); else sheet.setAttribute('open', '');
          document.documentElement.style.overflow = 'hidden';
        });
      });
      var close = function () {
        if (typeof sheet.close === 'function' && sheet.open) sheet.close(); else sheet.removeAttribute('open');
        discard();
        document.documentElement.style.overflow = '';
      };
      this.querySelectorAll('[data-pg-sheet-close]').forEach(function (b) { self.on(b, 'click', close); });
      this.on(sheet, 'close', function () { discard(); document.documentElement.style.overflow = ''; });
      this.on(sheet, 'click', function (e) { if (e.target === sheet) close(); });
      /* Back to this page: Chrome restores the ticks the form held when the shopper left (after this script runs,
         so not at connect), and the back/forward cache keeps the sheet open as APPLY left it; either way the sheet
         shows this page's applied filters. A sheet the shopper opened before the load event is left alone. */
      this.on(window, 'pageshow', function (e) {
        if (sheet.open || sheet.hasAttribute('open')) { if (e.persisted) close(); return; }
        discard();
      });
      this._off.push(function () { document.documentElement.style.overflow = ''; });
    }

    var more = this.querySelector('[data-pg-more]');
    /* A result's identity: the path of its product link (else its first link). */
    var itemPath = function (li) {
      var a = li.querySelector('a[href*="/products/"]') || li.querySelector('a[href]');
      if (!a) return '';
      try { return new URL(a.getAttribute('href'), window.location.href).pathname; } catch (e) { return ''; }
    };
    var listedPaths = function (list) {
      var seen = {};
      Array.prototype.forEach.call(list.children, function (li) { var path = itemPath(li); if (path) seen[path] = true; });
      return seen;
    };
    var storeKey = 'xz-pg-more:' + window.location.pathname + window.location.search;
    var store = null;
    try { store = window.sessionStorage; } catch (e) { store = null; }
    if (more && store) {
      /* Back after LOAD MORE: put the appended results (and the next link) back, then the saved scroll */
      var navType = '';
      try {
        var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        navType = nav ? nav.type : (performance.navigation && performance.navigation.type === 2 ? 'back_forward' : '');
      } catch (e) { navType = ''; }
      var saved = null;
      try { saved = JSON.parse(store.getItem(storeKey) || 'null'); } catch (e) { saved = null; }
      var listEl = this.querySelector('[data-pg-items]');
      if (saved && navType === 'back_forward' && listEl && saved.items) {
        /* Shopify's relevance order is not stable between requests, so the page 1 served on Back can differ from
           the one the results were loaded onto: the list comes back exactly as it was left (page 1 as shown, then
           what LOAD MORE appended), never with a result twice or one missing (site-r3-2). */
        if (saved.first) {
          var firstDoc = new DOMParser().parseFromString('<ul>' + saved.first + '</ul>', 'text/html').querySelector('ul');
          if (firstDoc && firstDoc.firstElementChild) {
            while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
            while (firstDoc.firstElementChild) listEl.appendChild(firstDoc.firstElementChild);
          }
        }
        var restoreDoc = new DOMParser().parseFromString('<ul>' + saved.items + '</ul>', 'text/html');
        var restored = restoreDoc.querySelector('ul');
        var restoredSeen = listedPaths(listEl);
        while (restored && restored.firstElementChild) {
          var restoredItem = restored.firstElementChild;
          var restoredPath = itemPath(restoredItem);
          if (restoredPath && restoredSeen[restoredPath]) { restored.removeChild(restoredItem); continue; }
          if (restoredPath) restoredSeen[restoredPath] = true;
          listEl.appendChild(restoredItem);
        }
        more.innerHTML = saved.more || '';
        if (!more.firstElementChild && listEl.classList.contains('xz-pg-pagelist')) listEl.classList.add('xz-pg-pagelist--end');
        if (saved.y) {
          if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
          var y = saved.y;
          requestAnimationFrame(function () { window.scrollTo(0, y); });
          this.on(window, 'load', function () { if (Math.abs(window.pageYOffset - y) > 2) window.scrollTo(0, y); });
        }
      } else if (saved) {
        store.removeItem(storeKey);
        saved = null;
      }
      this._pgMore = saved || { items: '', more: '' };
      this.on(window, 'pagehide', function () {
        if (!self._pgMore || !self._pgMore.items) return;
        self._pgMore.y = window.pageYOffset;
        try { store.setItem(storeKey, JSON.stringify(self._pgMore)); } catch (e) {}
      });
    }
    if (more && window.fetch && window.DOMParser) {
      this.on(more, 'click', function onMore(event) {
        var link = event.target.closest('a[data-pg-more-link]');
        if (!link) return;
        event.preventDefault();
        if (link.getAttribute('aria-busy') === 'true') return;
        link.setAttribute('aria-busy', 'true');
        var list = self.querySelector('[data-pg-items]');
        var ctrl = window.AbortController ? new AbortController() : null;
        if (ctrl) self._aborts.push(ctrl);
        var parse = function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('noscript').forEach(function (n) { n.remove(); });
          return doc;
        };
        var get = function (url) {
          return fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
            .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); });
        };
        get(link.href)
          .then(function (html) {
            var doc = parse(html);
            var incoming = doc.querySelector('[data-pg-items]');
            if (!incoming || !list) throw new Error('missing');
            /* Shopify's relevance order is not stable between requests (site-r3-2): the page fetched here can rank
               a result that the page already lists, which then pushes another one out of both. When that happens,
               the page before it is fetched in the same order once, and what it holds that the list lacks is
               added first, so no result is listed twice or lost. */
            var seen = listedPaths(list);
            var repeated = Array.prototype.some.call(incoming.children, function (li) { var path = itemPath(li); return path && seen[path]; });
            var pageUrl = new URL(link.href, window.location.href);
            var pageNum = parseInt(pageUrl.searchParams.get('page'), 10) || 0;
            if (!repeated || pageNum < 2) return { doc: doc, extra: [] };
            pageUrl.searchParams.set('page', String(pageNum - 1));
            return get(pageUrl.href).then(function (prevHtml) {
              var prevItems = parse(prevHtml).querySelector('[data-pg-items]');
              return { doc: doc, extra: prevItems ? Array.prototype.slice.call(prevItems.children) : [] };
            }, function (error) {
              if (error && error.name === 'AbortError') throw error;
              return { doc: doc, extra: [] };
            });
          })
          .then(function (res) {
            var doc = res.doc;
            var incoming = doc.querySelector('[data-pg-items]');
            var nextMore = doc.querySelector('[data-pg-more]');
            var seen = listedPaths(list);
            /* The list as it was before the first LOAD MORE (page 1 as served), for Back. Only what is appended is
               stored after it. */
            if (self._pgMore && !self._pgMore.first) self._pgMore.first = list.innerHTML;
            var first = null;
            var appended = '';
            var add = function (li, recovered) {
              var path = itemPath(li);
              if (recovered && !path) return;
              if (path && seen[path]) return;
              if (path) seen[path] = true;
              appended += li.outerHTML;
              if (!first) first = li;
              list.appendChild(li);
            };
            res.extra.forEach(function (li) { add(li, true); });
            Array.prototype.slice.call(incoming.children).forEach(function (li) { add(li, false); });
            more.innerHTML = nextMore ? nextMore.innerHTML : '';
            if (!more.firstElementChild && list.classList.contains('xz-pg-pagelist')) list.classList.add('xz-pg-pagelist--end');
            if (self._pgMore && store) {
              self._pgMore.items += appended;
              self._pgMore.more = more.innerHTML;
              try { store.setItem(storeKey, JSON.stringify(self._pgMore)); } catch (e) {}
            }
            if (first) { var focusable = first.querySelector('a'); if (focusable) focusable.focus({ preventScroll: true }); }
            var status = self.querySelector('[data-pg-status]');
            if (status) status.textContent = 'More results loaded';
          })
          .catch(function () { window.location.href = link.href; });
      });
    }
  };

  /* Reviews: the Judge.me block keeps a one-screen reserve until the widget has finished its setup, so the
     sections below never paint in the first viewport and then jump (Judge.me marks the widget done-setup). */
  XzPgSection.prototype.initReviews = function () {
    if (!this.classList.contains('xz-pg-reviews')) return;
    var self = this;
    var frame = this.querySelector('.xz-pg-reviews-widget');
    /* The widget's app blocks, measured without the reload/Back reservation (min-height on the frame). */
    var live = function () {
      return frame ? Array.prototype.filter.call(frame.children, function (n) { return n.tagName !== 'SCRIPT'; }) : [];
    };
    var liveDone = function () { return live().some(function (n) { return !!n.querySelector('.jdgm--done-setup-widget'); }); };
    var liveHeight = function () {
      var l = live();
      if (!l.length || !liveDone()) return 0;
      return Math.round(l[l.length - 1].getBoundingClientRect().bottom - l[0].getBoundingClientRect().top);
    };
    this.initReviewsHold(frame, liveDone, liveHeight);
    var ready = function () { self.classList.add('xz-pg--jdgm-ready'); };
    if (liveDone()) { ready(); return; }
    if (!('MutationObserver' in window)) { ready(); return; }
    var mo = new MutationObserver(function () { if (liveDone()) { ready(); mo.disconnect(); } });
    mo.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    this._observers.push(mo);
    var t = setTimeout(function () { ready(); mo.disconnect(); }, 12000);
    this._off.push(function () { clearTimeout(t); });
  };

  /* Reload / Back: on pagehide the settled widget's height is stored for this page and width, unless the shopper
     changed the widget (sort, tab, page, load more, form: it comes back in its first state). The section's inline
     script reserves it while parsing and keeps the widget transparent (html.xz-pg-reviews-hold); it shows once
     Judge.me has built it and its height has held for two checks, never before the load event (Chrome re-applies
     the saved offset there), or 6s after load at the latest. */
  XzPgSection.prototype.initReviewsHold = function (frame, liveDone, liveHeight) {
    if (!frame) return;
    var root = document.documentElement;
    var key = 'xz-pg-reviews-h:' + location.pathname + ':' + window.innerWidth;
    var changed = false;
    var mark = function () { changed = true; };
    this.on(frame, 'click', mark);
    this.on(frame, 'change', mark);
    this.on(window, 'pagehide', function () {
      try {
        if (changed) { sessionStorage.removeItem(key); return; }
        if (root.classList.contains('xz-pg-reviews-hold')) return;
        var h = liveHeight();
        if (h > 0) sessionStorage.setItem(key, String(h));
      } catch (e) {}
    });
    if (!root.classList.contains('xz-pg-reviews-hold')) return;
    var lastH = -1, timer = null, force = null;
    var release = function () {
      clearTimeout(timer); clearTimeout(force);
      root.classList.remove('xz-pg-reviews-hold');
      root.style.removeProperty('--xz-pg-reviews-h');
    };
    var check = function () {
      var h = liveDone() ? liveHeight() : 0;
      if (!h || h !== lastH) { lastH = h; timer = setTimeout(check, 250); return; }
      release();
    };
    var afterLoad = function () { timer = setTimeout(check, 0); force = setTimeout(release, 6000); };
    this._off.push(function () { clearTimeout(timer); clearTimeout(force); });
    if (document.readyState === 'complete') afterLoad(); else this.on(window, 'load', afterLoad, { once: true });
  };

  customElements.define('xz-pg-section', XzPgSection);
})();
